import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { AppContext } from '../../App';
import { getProductDetailsOptimized } from '../../clients/product_client';
import {
  getProductImages,
  getProductInventoryId,
  mergeProductImages,
} from '../../utils/productDisplay';
import './ProductComponentStyle.css';
import InventoryModal from './InventoryModal';
import ShopModal from '../Shop/ShopModal';
import { Spinner, Modal } from 'react-bootstrap';
import { OrderContext } from '../../pages/Order/OrdersPage';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'ProductDetailModal';

/**
 * Keep lots already loaded in the inventory modal when the list row has none.
 * An empty list `items` array would otherwise replace the modal lots and zero Opened.
 * `opened_lot_count` from the list row still merges through.
 *
 * @param {object|null|undefined} detail - Product currently shown in the modal
 * @param {object|null|undefined} listRow - Product row from the list
 * @returns {Array|undefined} Lots to keep, or undefined to use the merged row's items
 */
function loadedLotsToKeep(detail, listRow) {
  const detailHasLots = Array.isArray(detail?.items) && detail.items.length > 0;
  const listHasLots = Array.isArray(listRow?.items) && listRow.items.length > 0;
  if (!detailHasLots || listHasLots) {
    return undefined;
  }
  logInfo(LOG_CONTEXT, 'kept loaded stock items', {
    productId: detail?.id ?? listRow?.id ?? null,
    loadedItemCount: detail.items.length,
    listItemCount: Array.isArray(listRow?.items) ? listRow.items.length : null,
  });
  return detail.items;
}

/**
 * Fetches product details and displays a modal based on the provided props.
 *
 * @param {boolean} isSupplier - Whether the user is a supplier.
 * @param {object} productObj - Product details object.
 * @param {number} productId - Product ID.
 * @param {boolean} shopView - Determines if the modal is for shopping context.
 * @param {function} updateProducts - Function to update products.
 * @param {boolean} showShopModal - Determines if the shop modal is shown.
 * @param {function} setShowShopModal - Sets the show state of the shop modal.
 * @param {function} clearSearchValue - Function to clear search value.
 * @param {function} handleProductUpdateFromSupplierTable - Function to re-fetch the supplier list.
 * @param {number} inventoryModalButtonLargeFontSize - Font size for inventory modal button.
 * @param {import('react').ReactNode} [linkChildren] - Optional stable trigger content (e.g. AI chat product rows).
 * @param {boolean} [hideTrigger=false] - Hide the link button (use with controlled `show`).
 * @param {boolean} [show] - Controlled InventoryModal visibility.
 * @param {function} [onHide] - Called when controlled modal closes.
 *
 * @return {Component} - ProductDetailModal component.
 */
const ProductDetailModal = ({
  isSupplier,
  productObj,
  productId,
  shopView = false,
  isCatalogueView = false,
  updateProducts,
  showShopModal,
  setShowShopModal,
  clearSearchValue,
  handleProductUpdateFromSupplierTable,
  inventoryModalButtonLargeFontSize,
  linkChildren,
  hideTrigger = false,
  show,
  onHide,
  triggerClassName = '',
  /** Prefetch full product on trigger hover (disable in dense lists, e.g. order modal). */
  prefetchOnHover = true,
  /**
   * When set, the inventory modal can place a lot into this shelf, drawer,
   * rack, or box. `lockedSlotIndex` is the chart cell. Null means the banner
   * asks for a slot only when the destination is a slotted box.
   * @type {{ box: Object, lockedSlotIndex: number|null, onPlaced?: Function }|null}
   */
  placeTarget = null,
}) => {
  // Access token from context for authenticated API requests.
  const { token } = useContext(AppContext);
  const { orderUpdated, setOrderUpdated } = useContext(OrderContext);

  // Ref to track the loading state of the component.
  const isLoadingRef = useRef(false);
  // Ref to track if product has been fetched to prevent duplicate requests
  const hasFetchedProductRef = useRef(false);
  // Ref to track previous productObj to prevent unnecessary updates
  const previousProductObjRef = useRef(null);

  // State to store the product details.
  const [product, setProduct] = useState(() =>
    productObj
      ? { ...productObj, images: getProductImages(productObj) }
      : null
  );
  const [shopProductLoading, setShopProductLoading] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const productIdToUse = productObj
    ? shopView
      ? getProductInventoryId(productObj) ?? productObj.id
      : productObj.id
    : productId;

  useEffect(() => {
    hasFetchedProductRef.current = false;
    isLoadingRef.current = false;
    setProductNotFound(false);
  }, [productIdToUse]);

  // UseEffect to update the state on re-renders
  useEffect(() => {
    // Only update if productObj actually changed and is different from previous
    if (productObj && productObj !== previousProductObjRef.current) {
      previousProductObjRef.current = productObj;

      // Only update if the new productObj has more complete data
      // Preserve existing data (especially images) if the new productObj is missing critical information
      if (product && productObj) {
        const shouldUpdate =
          // Update if new productObj has images and current doesn't
          (productObj.images &&
            productObj.images.length > 0 &&
            (!product.images || product.images.length === 0)) ||
          // Update if new productObj has more complete data
          (productObj.items &&
            productObj.items.length > 0 &&
            (!product.items || product.items.length === 0)) ||
          // Update if new productObj has more fields
          Object.keys(productObj).length > Object.keys(product).length;

        // CRITICAL: Never update if it would lose images
        const wouldLoseImages =
          product.images && product.images.length > 0 && !productObj.images;
        if (wouldLoseImages) {
          return; // Exit early, don't update anything
        }

        // Allow updates for basic fields (name, category, etc.) but preserve images
        const shouldAllowBasicUpdate =
          !productObj.images && product.images && product.images.length > 0;

        if (shouldUpdate) {
          const keptItems = loadedLotsToKeep(product, productObj);
          setProduct(
            keptItems
              ? {
                  ...productObj,
                  items: keptItems,
                  images:
                    product.images && product.images.length > 0
                      ? product.images
                      : productObj.images,
                }
              : productObj
          );
        } else if (shouldAllowBasicUpdate) {
          // Update basic fields but preserve images
          const keptItems = loadedLotsToKeep(product, productObj);
          const mergedProduct = {
            ...product, // Keep existing data (including images)
            ...productObj, // Override with new basic data
            images: product.images, // Always preserve existing images
            ...(keptItems ? { items: keptItems } : {}),
          };
          setProduct(mergedProduct);
        } else {
          // Merge only the fields that are present in productObj, preserving existing images and other data
          const keptItems = loadedLotsToKeep(product, productObj);
          const mergedProduct = {
            ...product, // Keep existing data (including images)
            ...productObj, // Override with new data
            images: product.images || productObj.images || [], // Always preserve images
            ...(keptItems ? { items: keptItems } : {}),
          };
          setProduct(mergedProduct);
        }
      } else if (productObj && !product) {
        // If no current product, set with basic info but preserve images when they come
        // Set basic product info so names display properly, but preserve images when they come
        setProduct({
          ...productObj,
          images: getProductImages(productObj),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productObj]); // Only depend on productObj to prevent infinite loop

  // Function to fetch product details from the server.
  const fetchProduct = useCallback(
    (options = {}) => {
      const { force = false } = options;
      if (hasFetchedProductRef.current && !force) return;

      isLoadingRef.current = true;
      hasFetchedProductRef.current = true;
      if (shopView) {
        setShopProductLoading(true);
      }

      const currentProduct = product;

      getProductDetailsOptimized(token, productIdToUse, newProductData => {
        if (currentProduct && newProductData) {
          const mergedProduct = {
            ...currentProduct,
            ...newProductData,
            images: mergeProductImages(currentProduct, newProductData),
          };
          setProduct(mergedProduct);
          setProductNotFound(false);
        } else if (newProductData) {
          setProduct({
            ...newProductData,
            images: mergeProductImages(currentProduct, newProductData),
          });
          setProductNotFound(false);
        }
        isLoadingRef.current = false;
        setShopProductLoading(false);
      }).then(result => {
        if (result?.success === false) {
          if (result.status === 404) {
            setProductNotFound(true);
            setProduct(null);
          }
        }
      });
    },
    [token, productIdToUse, product, shopView]
  );

  useEffect(() => {
    if (!shopView || !showShopModal || !productIdToUse) return;
    hasFetchedProductRef.current = false;
    fetchProduct({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopView, showShopModal, productIdToUse]);

  // Controlled open (e.g. AI chat citation table): fetch immediately so InventoryModal can render.
  useEffect(() => {
    if (hideTrigger && show && productIdToUse && !hasFetchedProductRef.current) {
      fetchProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideTrigger, show, productIdToUse]);

  // Removed automatic fetching - now only fetch when user interacts with the product

  // useEffect to handle order updates
  useEffect(() => {
    if (orderUpdated) {
      fetchProduct();
      setOrderUpdated(false);
    }
  }, [orderUpdated, fetchProduct, setOrderUpdated]);

  // Function to handle product edits and fetch updated data.
  const handleEdit = () => {
    if (updateProducts) updateProducts();
    hasFetchedProductRef.current = false;
    fetchProduct({ force: true });
  };

  const displayProduct =
    product ||
    (productObj
      ? { ...productObj, images: getProductImages(productObj) }
      : productIdToUse
        ? { id: productIdToUse, name: `Product ${productIdToUse}`, items: [] }
        : null);

  // Render the appropriate modal based on 'shopView'.
  return (
    <div>
      {shopView ? (
        product ? (
          <ShopModal
            product={product}
            handleEdit={handleEdit}
            isCatalogueView={isCatalogueView}
            productLoading={shopProductLoading}
            show={showShopModal}
            setShow={setShowShopModal}
          />
        ) : hideTrigger ? (
          <Modal show={!!show} onHide={onHide} centered>
            <Modal.Body className="d-flex align-items-center justify-content-center gap-2 py-4">
              <Spinner
                size="sm"
                as="span"
                animation="border"
                role="status"
                aria-label="Loading product"
              />
              <span>Loading product…</span>
            </Modal.Body>
          </Modal>
        ) : null
      ) : productNotFound ? (
        <Modal show={!!show} onHide={onHide} centered>
          <Modal.Body className="text-center py-4">
            <p className="mb-0">Product not found.</p>
          </Modal.Body>
        </Modal>
      ) : displayProduct ? (
        <InventoryModal
          token={token}
          isSupplier={isSupplier}
          product={displayProduct}
          handleEdit={handleEdit}
          updateProducts={updateProducts}
          clearSearchValue={clearSearchValue}
          fetchProduct={fetchProduct}
          handleProductUpdateFromSupplierTable={
            handleProductUpdateFromSupplierTable
          }
          inventoryModalButtonLargeFontSize={
            inventoryModalButtonLargeFontSize
          }
          linkChildren={linkChildren}
          hideTrigger={hideTrigger}
          show={show}
          onHide={onHide}
          triggerClassName={triggerClassName}
          prefetchOnHover={prefetchOnHover}
          placeTarget={placeTarget}
        />
      ) : hideTrigger ? (
        <Modal show={!!show} onHide={onHide} centered>
          <Modal.Body className="d-flex align-items-center justify-content-center gap-2 py-4">
            <Spinner
              size="sm"
              as="span"
              animation="border"
              role="status"
              aria-label="Loading product"
            />
            <span>Loading product…</span>
          </Modal.Body>
        </Modal>
      ) : null}
    </div>
  );
};

export default ProductDetailModal;
